import { buildS3ProxySrcUrl, generateSizes, generateSrcSet, isOptimizableUrl, isS3Url } from 'core/utils/imageOptimization';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import * as coreEntities from 'core/store/index';

// Secuencia de fallback cuando availableSizes es null (no se sabe qué variantes existen):
// 1º intento: 800px; 2º intento: 400px; agotado → original
const NULL_SIZE_SEQUENCE = [[800], [400]];

function useImage({ src, srcSet, disableOptimization, responsiveSizes, sizes, availableSizes = null }) {
  const [maxSizeIndex, setMaxSizeIndex] = useState(0);
  const authToken = useSelector((state) => coreEntities.auth.selectors.getAuthToken(state));

  // Reset maxSizeIndex cuando cambia el src
  useEffect(() => {
    setMaxSizeIndex(0);
  }, [src]);

  // Detectar si es URL local (no optimizable)
  const isLocalUrl = src ? !isOptimizableUrl(src) : false;

  // Si hay srcSet manual o está deshabilitada la optimización, usar directamente
  if (srcSet || disableOptimization || !src || isLocalUrl) {
    return {
      imageSrc: src || '',
      imageSrcSet: srcSet || '',
      sizesString: sizes || '',
      handleImageError: () => {},
    };
  }

  // Número total de intentos según availableSizes:
  // - Con lista propia: un intento por cada tamaño (se quita el mayor en cada fallo)
  // - Con null: NULL_SIZE_SEQUENCE.length intentos
  const totalAttempts = availableSizes !== null ? availableSizes.length : NULL_SIZE_SEQUENCE.length;
  const hasExhaustedAllSizes = maxSizeIndex >= totalAttempts;

  // Calcular los tamaños del intento actual:
  // - Con availableSizes: slice progresivo (quita el mayor al final en cada intento)
  //   maxSizeIndex=0 → todos, maxSizeIndex=1 → sin el mayor, etc.
  // - Con null: usa NULL_SIZE_SEQUENCE[maxSizeIndex]
  let currentSizes;
  if (!hasExhaustedAllSizes) {
    if (availableSizes !== null) {
      currentSizes =
        maxSizeIndex > 0 ? availableSizes.slice(0, availableSizes.length - maxSizeIndex) : availableSizes;
    } else {
      currentSizes = NULL_SIZE_SEQUENCE[maxSizeIndex];
    }
  }

  // Generar srcSet con los tamaños del intento actual
  const generatedSrcSet = hasExhaustedAllSizes ? '' : generateSrcSet(src, currentSizes, 0, null, authToken) || '';

  // Generar sizes string solo si hay srcSet generado
  const sizesString = hasExhaustedAllSizes ? '' : sizes || generateSizes(responsiveSizes);

  // Handler de error: avanza al siguiente intento de la secuencia
  const handleImageError = () => {
    if (!src || isLocalUrl) return;
    setMaxSizeIndex((prev) => (prev < totalAttempts ? prev + 1 : prev));
  };

  // Para URLs S3 privadas, el src debe apuntar también al proxy para que
  // el navegador pueda cargar la imagen (el S3 directo es privado)
  const proxySrc = isS3Url(src) && authToken ? buildS3ProxySrcUrl(src, authToken) : null;

  return {
    imageSrc: proxySrc || src,
    imageSrcSet: hasExhaustedAllSizes ? '' : generatedSrcSet,
    sizesString,
    handleImageError,
  };
}

export default useImage;
